import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { CityPicker } from "@/components/CityPicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SpecializationPicker } from "@/components/SpecializationPicker";
import { specializationsOf } from "@shared/specializations";

export const LANGS = ["English", "Hindi", "Tamil", "Telugu", "Marathi", "Bengali", "Gujarati", "Kannada", "Malayalam", "Punjabi"];

/** Editable public-profile fields in a popup. `onSave(draft)` performs the existing updateProfile call; the dialog closes only when it resolves. */
export function ProfileEditDialog({ open, profile, onClose, onSave }) {
  const [d, setD] = useState(profile);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setD({ ...profile, specializations: specializationsOf(profile) }); }, [open, profile]);

  const toggleLang = (lng) => {
    const next = new Set(d.languages || []);
    next.has(lng) ? next.delete(lng) : next.add(lng);
    setD({ ...d, languages: Array.from(next) });
  };
  const save = async () => {
    setBusy(true);
    try { await onSave(d); onClose(); } catch { /* toast shown by caller; keep dialog open */ }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl p-5 sm:p-6" data-testid="profile-edit-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Edit public profile</DialogTitle>
          <DialogDescription className="text-xs">What clients see on your listing.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Full name</Label>
            <Input value={d.name || ""} onChange={(e) => setD({ ...d, name: e.target.value })} className="mt-1" data-testid="profile-name" />
          </div>
          <div>
            <Label className="text-xs">Specializations</Label>
            <SpecializationPicker value={d.specializations || []} onChange={(v) => setD({ ...d, specializations: v, specialization: v[0] || "" })} testid="profile-specialization" />
          </div>
          <div>
            <Label className="text-xs">City / Practice location</Label>
            <CityPicker
              testid="profile-city"
              serviceableOnly
              value={d.location?.city_id ? d.location : (d.city ? { city_id: null, city_name: d.city, state_name: "" } : null)}
              onChange={(c) => setD({
                ...d,
                location: c ? { city_id: c.id, city_name: c.name, state_name: c.state_name, lgd_code: c.lgd_code } : null,
                city: c ? c.name : "",
              })}
            />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input value={d.phone || ""} onChange={(e) => setD({ ...d, phone: e.target.value })} className="mt-1" data-testid="profile-phone" />
          </div>
          <div>
            <Label className="text-xs">Years of experience</Label>
            <Input type="number" value={d.experience ?? ""} onChange={(e) => setD({ ...d, experience: e.target.value })} className="mt-1" data-testid="profile-experience" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Languages</Label>
            <div className="mt-1 flex flex-wrap gap-2" data-testid="profile-languages">
              {LANGS.map((l) => (
                <button key={l} type="button" onClick={() => toggleLang(l)} aria-pressed={(d.languages || []).includes(l)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                          (d.languages || []).includes(l) ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "bg-white border-slate-200 text-slate-600 hover:border-[#0A2540]"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Bio</Label>
            <textarea value={d.bio || ""} onChange={(e) => setD({ ...d, bio: e.target.value })} data-testid="profile-bio" rows={4}
                      className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#0A2540]"
                      placeholder="Specialties, courts you practice in, notable wins…" />
          </div>
        </div>
        <div className="mt-2 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="profile-edit-cancel" className="rounded-xl font-bold">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="profile-save-btn" className="rounded-xl adv-btn-primary font-bold">
            <Save size={14} /> {busy ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
